import { encounterResponseSchema, type EncounterResponse } from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * Encounters (`server/src/clinical/encountersRoutes.ts`, requiring `chart:read`/`chart:write`).
 *
 * **Not wrapped, unlike `api/patients.ts` and `api/chart.ts`.** `encountersRoutes.ts`'s own
 * `respond()` returns `c.json(result.value, successStatus)` directly — `result.value` is already
 * `EncounterResponse` (single) or `readonly EncounterResponse[]` (list) — so the wire shape is a
 * bare object or a bare array, not `{ encounter: ... }` / `{ encounters: [...] }`. Verified by
 * reading `encountersService.ts`'s own return types (`EncounterResult<EncounterResponse>` for
 * create/get/update, `EncounterResult<readonly EncounterResponse[]>` for list) rather than assumed
 * from the sibling files that do wrap — `claude-kit/rules/working-style.md`'s "Do not guess
 * locations" applied to a response shape, not just a file path.
 *
 * Only listing is wired here. Starting or updating an encounter (`chart:write`) is out of this
 * task's scope — the note composer picks from a patient's *existing* encounters, it does not create
 * one.
 */
export async function listEncounters(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly EncounterResponse[]> {
  return apiRequest(
    `/api/patients/${patientId}/encounters`,
    z.array(encounterResponseSchema),
    signal === undefined ? {} : { signal },
  );
}
